import { ApplicationUser, CreateUserRequest, DefaultRoles } from "@api/apiClient";
import { useDeleteUserRoles, useUsers } from "@api/users";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import { Box, Snackbar, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridDeleteIcon, GridRowId } from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

export interface AdminUserGridProps {
  role: DefaultRoles;
}

const AdminsGrid = observer((props: AdminUserGridProps) => {
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [openDeleteToast, setOpenDeleteToast] = useState(false);
  const [rows, setRows] = useState<ApplicationUser[]>([]);
  const adminUsers = useUsers(props.role);
  const deleteUserClaims = useDeleteUserRoles();
  const confirm = useConfirm();

  useEffect(() => {
    const newRows = adminUsers.data?.map((row: any, index: number) => ({ ...row, id: index })) || [];
    setRows(newRows);
  }, [adminUsers.data]);

  const handleDeleteClick = async (id: GridRowId) => {
    const { confirmed } = await confirm({
      title: i18n.t("admin-tab.confirm-dialog.header"),
      description: i18n.t("admin-tab.confirm-dialog.message"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (confirmed) {
      const deletingRow: any = rows.find((row: any) => row.id === id);
      if (deletingRow?.email) {
        const createUserRequest: any = new CreateUserRequest({
          email: deletingRow.email,
          role: DefaultRoles.Admin,
        });

        await deleteUserClaims.mutateAsync(createUserRequest);
        setOpenDeleteToast(true);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("admin-tab.grid.header-name"),
      editable: false,
      flex: 1,
    },
    {
      field: "email",
      headerName: i18n.t("admin-tab.grid.header-email"),
      editable: false,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("admin-tab.grid.header-action"),
      resizable: false,
      width: 80,
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            key={`delete-${id}`}
            icon={
              <Tooltip title={i18n.t("admin-tab.grid.delete-icon-tooltip")}>
                <GridDeleteIcon />
              </Tooltip>
            }
            label={i18n.t("admin-tab.grid.delete-icon-title")}
            onClick={() => handleDeleteClick(id)}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
        <DataGrid
          loading={adminUsers.isFetching || adminUsers.isPending}
          columns={columns}
          rows={rows}
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [{ field: "email", sort: "asc" }],
            },
          }}
          sx={{ height: `${(height || 400) - 16}px` }}
        />
      </Box>
      <Snackbar
        open={openDeleteToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("admin-tab.grid.delete-success-message")}
        onClose={() => setOpenDeleteToast(false)}
      />
    </>
  );
});

export default AdminsGrid;
